/**
 * ChartView (web variant) — renders Chart.js charts via react-chartjs-2.
 *
 * Native uses an HTML-injected WebView (see ChartView.tsx). On web we render
 * the Chart components directly since we already have a DOM. This avoids
 * shipping `react-native-webview` to the web bundle and removes the iframe-
 * style isolation in favor of a real React tree.
 *
 * Lifted from frontend/client/src/Components/InlineChart/index.js — same
 * Chart.js setup, same six chart types, same options pattern. Differences:
 * - Receives the same `chartPayload` shape that ChartView.tsx accepts so
 *   call sites are platform-agnostic.
 * - Pulls colors from the RN theme so the chart matches dark/light mode.
 * - Imports chartjs-adapter-date-fns so `type:"time"` axes work without a
 *   CDN script tag (matches the WebView's inline adapter).
 */

import React from 'react';
import { Text, View } from 'react-native';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  RadialLinearScale,
  TimeScale,
} from 'chart.js';
import { Bar, Line, Pie, Doughnut, Radar, PolarArea } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { useTheme } from '../hooks/useTheme';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  RadialLinearScale,
  TimeScale,
);

type ChartType = 'bar' | 'line' | 'pie' | 'doughnut' | 'radar' | 'polarArea';

const CHART_COMPONENTS: Record<ChartType, any> = {
  bar: Bar,
  line: Line,
  pie: Pie,
  doughnut: Doughnut,
  radar: Radar,
  polarArea: PolarArea,
};

const SCALELESS_TYPES: ReadonlyArray<ChartType> = ['pie', 'doughnut', 'radar', 'polarArea'];

interface ChartViewProps {
  chartPayload: {
    title?: string;
    config: {
      type: string;
      data: object;
      options?: Record<string, any>;
    };
  };
}

export default function ChartView({ chartPayload }: ChartViewProps) {
  const theme = useTheme();

  if (!chartPayload?.config?.type || !chartPayload?.config?.data) {
    return null;
  }

  const type = String(chartPayload.config.type).toLowerCase() as ChartType;
  const ChartComponent = CHART_COMPONENTS[type];

  // Theme-aware Chart.js options. Caller-provided `config.options` wins so the
  // model can override e.g. axis titles, tick formatters, legend position.
  const isScaleless = SCALELESS_TYPES.includes(type);
  const options: Record<string, any> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { color: theme.colors.text } },
    },
    ...(!isScaleless
      ? {
          scales: {
            x: { ticks: { color: theme.colors.text }, grid: { color: theme.colors.border } },
            y: { ticks: { color: theme.colors.text }, grid: { color: theme.colors.border } },
          },
        }
      : {}),
    ...(chartPayload.config.options || {}),
  };

  return (
    <View
      style={{
        marginVertical: 8,
        borderRadius: 8,
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: theme.colors.border,
        backgroundColor: theme.colors.surface,
      }}
    >
      {chartPayload.title ? (
        <Text
          style={{
            fontSize: 14,
            fontWeight: '600',
            color: theme.colors.headerText,
            paddingHorizontal: 12,
            paddingTop: 10,
            paddingBottom: 4,
          }}
        >
          {chartPayload.title}
        </Text>
      ) : null}
      <View style={{ height: 260, padding: 8 }}>
        {ChartComponent ? (
          <ChartComponent data={chartPayload.config.data as any} options={options} />
        ) : (
          <Text style={{ color: theme.colors.muted, fontSize: 12, padding: 12 }}>
            Unsupported chart type: {chartPayload.config.type}
          </Text>
        )}
      </View>
    </View>
  );
}
